import { WhichWeightAPI } from 'WhichWeightAPI'
import { useState, useEffect, useMemo } from 'react'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid'
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts'
import moment from 'moment'

import OutlineButton from 'components/Buttons/OutlineButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'
import ScreenFrame from 'components/Frame/ScreenFrame'
import PaginationController from 'components/Pagination/PaginationController'
import SearchBar from 'components/Search/SearchBar'

export default function BranchClicks() {
    const [branchClickData, setBranchClickData] = useState(null)
    const [affiliateClicksLoading, setAffiliateClicksLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showFilters, setShowFilters] = useState(false)
    const [firstClicksOnly, setFirstClicksOnly] = useState(1)
    const [paginatedBranchClicks, setPaginatedBranchClicks] = useState([])
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 50,
        totalCount: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
    })

    const GetBranchClickData = async () => {
        try {
            setBranchClickData(null)

            const jsonBody = {
                firstClicksOnly: firstClicksOnly
            }

            // console.log(jsonBody)

            const result = await WhichWeightAPI("REPORTING", "GET", "/branch_data", jsonBody)

            // console.log(result)

            setBranchClickData(result.data.data)

        } catch (error) {
            console.error('Error fetching branch click data:', error)
        }
    }

    const EscapeCsvCell = (value) => {
        const s = value == null ? '' : String(value)
        if (/[",\n\r]/.test(s)) {
            return `"${s.replace(/"/g, '""')}"`
        }
        return s
    }

    const HandleExportCsv = (clicks, filenamePrefix = 'branch_clicks') => {
        const headers = ['Id', 'Email', 'Click Id', 'Campaign', 'Feature', 'Channel', 'Created Date']
        const rows = clicks.map((click) => [
            EscapeCsvCell(click.userId),
            EscapeCsvCell(click.email),
            EscapeCsvCell(click.clickId),
            EscapeCsvCell(click.campaign),
            EscapeCsvCell(click.feature),
            EscapeCsvCell(click.channel),
            EscapeCsvCell(moment.utc(click.createdTimestamp).local().format('MMMM DD, YYYY hh:mm A')),
        ])
        const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        link.setAttribute('href', url)
        link.setAttribute('download', `${filenamePrefix}_${moment().utc().format('YYYY-MM-DD-HH-mm-ss')}.csv`)
        link.style.visibility = 'hidden'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }

    const HandleExportAffiliateClicks = async () => {
        try {
            setAffiliateClicksLoading(true)

            const result = await WhichWeightAPI("REPORTING", "GET", "/affiliate_branch_clicks")
            const affiliateClicks = result.data.data ?? []

            HandleExportCsv(affiliateClicks, 'affiliate_branch_clicks')
        } catch (error) {
            console.error('Error fetching affiliate branch clicks:', error)
        } finally {
            setAffiliateClicksLoading(false)
        }
    }

    const OpenAthleteProfileInNewTab = (athleteId) => {
        const url = `/athlete/${athleteId}`
        window.open(url, '_blank')
    }

    const HandleCellTargetedSearch = (searchKey, value) => {
        if (value == null || value === '') {
            setSearchTerm(`${searchKey}:`)
            return
        }
        setSearchTerm(`${searchKey}:${value}`)
    }

    const HandlePageSizeChange = (newPageSize) => {
        setPagination(prev => ({
            ...prev,
            pageSize: parseInt(newPageSize),
            page: 1
        }))
    }

    const filteredBranchClicks = useMemo(() => {
        if (branchClickData === null) return []
        const normalizedSearchTerm = searchTerm.toLowerCase().trim()
        if (normalizedSearchTerm === '') return branchClickData

        const targetedSearchKeyMap = {
            id: (click) => click.userId,
            email: (click) => click.email,
            clickid: (click) => click.clickId,
            campaign: (click) => click.campaign,
            feature: (click) => click.feature,
            channel: (click) => click.channel,
            createddate: (click) => moment.utc(click.createdTimestamp).local().format('MMMM DD, YYYY hh:mm A')
        }

        const colonIndex = normalizedSearchTerm.indexOf(':')
        if (colonIndex > 0) {
            const rawKey = normalizedSearchTerm.slice(0, colonIndex).trim()
            const rawValue = normalizedSearchTerm.slice(colonIndex + 1).trim()
            const targetValueGetter = targetedSearchKeyMap[rawKey]

            if (targetValueGetter !== undefined && rawValue !== '') {
                return branchClickData.filter((click) =>
                    (targetValueGetter(click) ?? '').toString().toLowerCase().includes(rawValue)
                )
            }
        }

        return branchClickData.filter((click) => {
            const searchableValues = [
                click.userId,
                click.email,
                click.clickId,
                click.campaign,
                click.feature,
                click.channel,
                moment.utc(click.createdTimestamp).local().format('MMMM DD, YYYY hh:mm A')
            ]

            return searchableValues.some((value) => (value ?? '').toString().toLowerCase().includes(normalizedSearchTerm))
        })
    }, [branchClickData, searchTerm])

    const campaignPieChartData = useMemo(() => {
        if (branchClickData === null || branchClickData.length === 0) return []

        const uniqueUserCampaignPairs = new Set()
        const campaignCounts = new Map()

        branchClickData.forEach((click) => {
            const userId = (click.userId ?? '').toString().trim()
            const campaign = (click.campaign ?? '').toString().trim()
            const normalizedCampaign = campaign.toLowerCase()
            const campaignLabel = campaign === '' ? '(No Campaign)' : campaign
            const uniquePairKey = `${userId}::${normalizedCampaign}`

            if (uniqueUserCampaignPairs.has(uniquePairKey)) return
            uniqueUserCampaignPairs.add(uniquePairKey)
            campaignCounts.set(campaignLabel, (campaignCounts.get(campaignLabel) ?? 0) + 1)
        })

        return Array.from(campaignCounts.entries())
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value)
    }, [branchClickData])

    useEffect(() => { GetBranchClickData() }, [firstClicksOnly])

    useEffect(() => {
        if (filteredBranchClicks.length > 0) {
            const totalCount = filteredBranchClicks.length
            const totalPages = Math.ceil(totalCount / pagination.pageSize) || 1
            const currentPage = Math.min(pagination.page, totalPages || 1)
            const startIndex = (currentPage - 1) * pagination.pageSize
            const endIndex = startIndex + pagination.pageSize

            setPagination(prev => ({
                ...prev,
                totalCount,
                totalPages,
                page: currentPage,
                hasNext: currentPage < totalPages,
                hasPrevious: currentPage > 1
            }))

            setPaginatedBranchClicks(filteredBranchClicks.slice(startIndex, endIndex))
            return
        }

        setPagination(prev => ({
            ...prev,
            totalCount: 0,
            totalPages: 0,
            page: 1,
            hasNext: false,
            hasPrevious: false
        }))
        setPaginatedBranchClicks([])
    }, [filteredBranchClicks, pagination.page, pagination.pageSize])

    if (branchClickData === null) {
        return (
            <ScreenFrame limitWidth={false}>
                <div className='flex items-center gap-x-4'>
                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex-grow">
                        Branch Link Dashboard
                    </h1>

                    <div className="w-40">
                        <OutlineButton text="Refresh" action={GetBranchClickData} className="shadow-md" />
                    </div>
                </div>
                
                <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='w-full h-[800px] bg-gray-200 rounded-lg animate-pulse' />
            </ScreenFrame>
        )
    }

    if (branchClickData.length === 0) {
        return (
            <ScreenFrame limitWidth={false}>
                <div className='flex items-center gap-x-4'>
                    <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex-grow">
                        Branch Link Dashboard
                    </h1>

                    <div className="w-40">
                        <OutlineButton text="Refresh" action={GetBranchClickData} className="shadow-md" />
                    </div>
                </div>

                <div className="p-4 text-gray-400 text-center">No branch click data found</div>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame limitWidth={false}>
            <div className='flex items-center gap-x-4'>
                <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex-grow">
                    Branch Link Dashboard
                </h1>

                <div className="w-40">
                    <OutlineButton text="Refresh" action={GetBranchClickData} className="shadow-md" />
                </div>
            </div>

            <div className="mb-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="p-4 bg-white border border-gray-300 rounded-md shadow-md">
                    <h2 className="text-base font-semibold text-gray-900 mb-3">Unique Campaign Clicks (Pie Chart)</h2>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={campaignPieChartData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={95}
                                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                >
                                    {campaignPieChartData.map((entry, index) => (
                                        <Cell
                                            key={`${entry.name}-${index}`}
                                            fill={['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#06b6d4', '#ec4899', '#84cc16'][index % 8]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="p-4 bg-white border border-gray-300 rounded-md shadow-md">
                    <h2 className="text-base font-semibold text-gray-900 mb-3">Unique Campaign Clicks (Bar Chart)</h2>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={campaignPieChartData} margin={{ top: 8, right: 0, left: -14, bottom: 12 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" angle={0} textAnchor="middle" interval={0} height={10} className='text-xxs' />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="value" fill="#f97316" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className='mb-4 flex items-center gap-4'>
                <div className='flex-grow'>
                    <SearchBar
                        placeholder="Search any column or use key:value (id, email, clickId, campaign, feature, channel, createdDate)"
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        showFilters={showFilters}
                        setShowFilters={setShowFilters}
                    />
                </div>

                <label className='flex items-center gap-x-2 px-3 py-2 bg-white border border-gray-200 rounded-md shadow-md whitespace-nowrap cursor-pointer'>
                    <input
                        type="checkbox"
                        className='h-4 w-4 cursor-pointer'
                        checked={firstClicksOnly === 1}
                        onChange={(e) => setFirstClicksOnly(e.target.checked ? 1 : 0)}
                    />
                    <span className='text-sm text-gray-700'>First Clicks Only</span>
                </label>

                <div className="w-28">
                    <BrandColorButton 
                        text="Export" 
                        action={() => HandleExportCsv(filteredBranchClicks)} 
                        className="shadow-md" 
                    />
                </div>

                <div className="w-48">
                    <BrandColorButton 
                        text="Export Affiliate Clicks" 
                        action={HandleExportAffiliateClicks} 
                        isLoading={affiliateClicksLoading}
                        className="shadow-md" 
                    />
                </div>
            </div>

            <div>
                <div
                    className='-mb-4 flex gap-4 transition-all duration-200 select-none'
                    style={{
                        maxHeight: showFilters ? '100px' : '0px',
                        opacity: showFilters ? 1 : 0,
                        paddingBottom: showFilters ? '1rem' : '0',
                        pointerEvents: showFilters ? 'auto' : 'none'
                    }}
                >
                    <div className='flex items-center gap-x-1'>
                        <p className='text-sm text-gray-700'>Items per page:</p>

                        <div className="border border-gray-200 rounded-md shadow-md">
                            <select
                                value={pagination.pageSize}
                                className="px-2 py-1 text-sm border-r-[10px] border-white rounded-md cursor-pointer focus:outline-none"
                                onChange={(e) => HandlePageSizeChange(e.target.value)}
                            >
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option value="200">250</option>
                                <option value="500">500</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <PaginationController pagination={pagination} setPagination={setPagination} />

            <div className="text-sm border border-gray-400 divide-y divide-gray-400 rounded overflow-hidden bg-white shadow-md">
                <div className='grid grid-cols-[0.5fr_3fr_2fr_2fr_2fr_2fr_3fr_0.4fr] bg-gray-200 font-semibold divide-x divide-gray-400'>
                    <div className='px-2 py-1 text-center'>Id</div>
                    <div className='px-2 py-1'>Email</div>
                    <div className='px-2 py-1'>Click Id</div>
                    <div className='px-2 py-1'>Campaign</div>
                    <div className='px-2 py-1'>Feature</div>
                    <div className='px-2 py-1'>Channel</div>
                    <div className='px-2 py-1'>Created Date</div>
                    <div className='px-1 py-1' />
                </div>

                {paginatedBranchClicks.map((click) => (
                    <div
                        key={click.id}
                        className="grid grid-cols-[0.5fr_3fr_2fr_2fr_2fr_2fr_3fr_0.4fr] divide-x divide-gray-400 hover:bg-gray-100"
                    >
                        <div className='px-2 py-1 truncate text-center cursor-pointer text-sm flex items-center justify-center' onClick={() => HandleCellTargetedSearch('id', click.userId)}>{click.userId}</div>
                        <div className='px-2 py-1 truncate cursor-pointer' title={click.email} onClick={() => HandleCellTargetedSearch('email', click.email)}>{click.email}</div>
                        <div className='px-2 py-1 truncate cursor-pointer' onClick={() => HandleCellTargetedSearch('clickId', click.clickId)}>{click.clickId}</div>
                        <div className='px-2 py-1 truncate cursor-pointer' title={click.campaign} onClick={() => HandleCellTargetedSearch('campaign', click.campaign)}>{click.campaign}</div>
                        <div className='px-2 py-1 truncate cursor-pointer' onClick={() => HandleCellTargetedSearch('feature', click.feature)}>{click.feature}</div>
                        <div className='px-2 py-1 truncate cursor-pointer' onClick={() => HandleCellTargetedSearch('channel', click.channel)}>{click.channel}</div>
                        <div className='px-2 py-1 truncate flex gap-x-2 cursor-pointer' onClick={() => HandleCellTargetedSearch('createdDate', moment.utc(click.createdTimestamp).local().format('MMMM'))}>
                            <div>{moment.utc(click.createdTimestamp).local().format('MMMM DD, YYYY')}</div>
                            <div className='text-xs text-gray-600 mt-0.5'>{moment.utc(click.createdTimestamp).local().format('hh:mm A')}</div>
                        </div>
                        <div className='px-1 py-1 flex items-center justify-center'>
                            <button
                                type="button"
                                className="text-gray-400"
                                onClick={() => OpenAthleteProfileInNewTab(click.userId)}
                                title="Open athlete profile"
                            >
                                <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <PaginationController pagination={pagination} setPagination={setPagination} />
        </ScreenFrame>
    )
}